import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch, ApiError } from '../lib/api';
import { decodeJwtPayload } from '../lib/decodeJwtPayload';
import { ERR_INDIVIDUAL_TIER_SOLO_ONLY, isSoloTier } from '../lib/tier';
import { isSectionVisible, getAccountSectionLabel } from '../lib/tier-features';
import Sidebar from '../components/Sidebar';
import { useMyTenant, isAdminRole, type EstimationType } from '../hooks/useMyTenant';
import TimezoneDropdown from '../components/TimezoneDropdown';
import PasswordInput from '../components/PasswordInput';
import i18n from '../i18n';
import { detectCurrency, formatTierPrice } from '../lib/currency';

// ── Types ────────────────────────────────────────────────────────────────────

interface TenantMemberRow {
  id: string;
  userId: string;
  role: string;
  userName: string | null;
  userEmail: string | null;
  createdAt: string;
  /** APPROVE.1a (#3183) — added through a partner link; managed by that link. */
  viaPartner?: boolean;
}

// #3229 Story A — GET /tenants/:id/members/:userId/removal-preview.
interface ProjectRef { id: string; name: string }
interface RemovalPreview {
  projects: Array<{ id: string; name: string; status: string; deleted: boolean; roles: Array<{ role: string; memberType: string }> }>;
  soleLeadProjects: ProjectRef[];
  stakeholderProjects: number;
  pendingInvitations: number;
  liveLinks: number;
  lastAdmin: boolean;
}

// #3229 O9 — GET /tenants/:id/partner-links/:partnerAccountId/end-preview.
interface EndPreview {
  departingMembers: number;
  projects: ProjectRef[];
  leaderlessProjects: ProjectRef[];
}

/** A 409 SOLE_PROJECT_LEAD body's project list, or null for any other error. */
function soleLeadProjectsOf(err: unknown): ProjectRef[] | null {
  if (!(err instanceof ApiError) || err.status !== 409 || err.body.code !== 'SOLE_PROJECT_LEAD') return null;
  return Array.isArray(err.body.projects) ? (err.body.projects as ProjectRef[]) : [];
}

// APPROVE.1a (#3183, ruling R-5) — GET /tenants/:id/partner-links
interface PartnerLinkRow {
  partnerAccountId: string;
  partnerName: string;
  deliveryModel: 'MANAGED' | 'SELF_SERVICE';
  linkedAt: string;
  youAreClientContact: boolean;
  openUnlinkRequest: { id: string; requestedAt: string } | null;
}

// #2428 — deliberately no `id`/token field. project_invitations.id IS the
// accept-invite bearer token (see tenants.ts's GET /:id/invitations
// handler); this row therefore carries nothing an admin action can key off.
interface PendingInvitationRow {
  email: string;
  role: string;
  status: string;
  createdAt: string;
}

interface Tenant {
  id: string;
  name: string;
  estimationType: EstimationType;
  agentConfigOverride: { timezone?: string } | null;
  // Sprint 12.5 ACC.B6 — opt-in feature flag for the per-section
  // thumbs-up/down feedback UI. Tenant Admin toggles this via the
  // checkbox below; ArtefactDetailContent gates rendering on it.
  qualityFeedbackEnabled?: boolean;
  // Sprint UXV UXV.F4 (#1420) — tenant-level default for the Claude Design
  // export toggle (Settings → Integrations). Projects can override per-project.
  claudeDesignExportEnabled?: boolean;
}


interface NotificationConfig {
  gateFailures: boolean;
  artefactApprovals: boolean;
  agentCompletions: boolean;
}

interface CredentialStatus {
  github: {
    connected: boolean;
    maskedToken: string | null;
    validatedAt: string | null;
    orgName: string | null;
  };
}

// ── Styles ───────────────────────────────────────────────────────────────────

const cardStyle: React.CSSProperties = {
  background: 'var(--surface)',
  border: '1px solid var(--border)',
  borderRadius: 'var(--radius-lg)',
  boxShadow: 'var(--shadow-sm)',
  padding: 24,
  marginBottom: 16,
};

const labelStyle: React.CSSProperties = {
  fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 600,
  color: 'var(--text)', marginBottom: 6, display: 'block',
};

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '8px 12px',
  fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text)',
  background: 'var(--surface)', border: '1px solid var(--border)',
  borderRadius: 'var(--radius-sm)', outline: 'none',
};

const btnPrimary: React.CSSProperties = {
  padding: '8px 16px', fontFamily: 'var(--font-display)', fontSize: 13,
  fontWeight: 600, color: 'white', background: 'var(--primary)',
  border: 'none', borderRadius: 'var(--radius-sm)', cursor: 'pointer',
  transition: 'box-shadow 150ms',
};

const btnGhost: React.CSSProperties = {
  padding: '6px 12px', fontFamily: 'var(--font-body)', fontSize: 12,
  fontWeight: 500, color: 'var(--error)', background: 'transparent',
  border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)',
  cursor: 'pointer',
};

// ── Component ────────────────────────────────────────────────────────────────

export default function SettingsOrganisationPage() {
  const navigate = useNavigate();
  const { myTenant, loading: tenantLoading } = useMyTenant();

  // Tenant details
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [orgName, setOrgName] = useState('');
  const [timezone, setTimezone] = useState('');
  const [notifications, setNotifications] = useState<NotificationConfig>({
    gateFailures: true, artefactApprovals: true, agentCompletions: false,
  });
  // Sprint 12.5 ACC.B6 — opt-in flag (Tenant Admin only).
  const [qualityFeedbackEnabled, setQualityFeedbackEnabled] = useState(false);
  // Sprint UXV UXV.F4 (#1420) — tenant-level Claude Design export default.
  const [claudeDesignExportEnabled, setClaudeDesignExportEnabled] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');

  // Members
  const [members, setMembers] = useState<TenantMemberRow[]>([]);
  const [membersLoading, setMembersLoading] = useState(true);

  // Pending invitations (#2428)
  const [pendingInvites, setPendingInvites] = useState<PendingInvitationRow[]>([]);
  const [pendingInvitesLoading, setPendingInvitesLoading] = useState(true);

  // Invite modal
  const [showInvite, setShowInvite] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<'TENANT_ADMIN' | 'TENANT_MEMBER'>('TENANT_MEMBER');
  const [inviting, setInviting] = useState(false);
  const [inviteMsg, setInviteMsg] = useState('');

  // Upgrade modal (Individual tier solo enforcement)
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const isIndividualTier = isSoloTier(myTenant?.subscriptionTier);
  // Per-tier UX (issue #779) — solo plans see "Account Profile" instead of
  // "Organisation Profile", and Team Members is hidden entirely (the upgrade
  // CTA moves to the Plan card, tracked in #780).
  const orgLabel = getAccountSectionLabel(myTenant?.subscriptionTier, 'page');

  // Remove modal
  const [removeTarget, setRemoveTarget] = useState<TenantMemberRow | null>(null);
  // #3229 Story A — what the removal will do (loaded when the modal opens),
  // and a refusal the DELETE answered (a sole lead the preview didn't show).
  const [removePreview, setRemovePreview] = useState<RemovalPreview | null>(null);
  const [removePreviewError, setRemovePreviewError] = useState<string | null>(null);
  const [removeError, setRemoveError] = useState<string | null>(null);
  const [removeBlockers, setRemoveBlockers] = useState<ProjectRef[]>([]);
  // Each preview request gets an id; opening the modal again supersedes it, so
  // a late answer for another member never lands (#3770 Codex P2). Remove stays
  // disabled until this member's own preview, or its failure, has loaded.
  const removeRequestRef = useRef(0);
  // #3229 O9 — the end-partnership preview, and the list the end returned.
  const [endPreview, setEndPreview] = useState<EndPreview | null>(null);
  const [endLeaderless, setEndLeaderless] = useState<{ partnerName: string; projects: ProjectRef[] } | null>(null);
  const [partnerLinks, setPartnerLinks] = useState<PartnerLinkRow[]>([]);
  const [endTarget, setEndTarget] = useState<PartnerLinkRow | null>(null);
  const [endError, setEndError] = useState<string | null>(null);
  const [myUserId] = useState<string | null>(() => {
    const jwt = localStorage.getItem('blueprint_token');
    return (jwt ? decodeJwtPayload<{ userId?: string }>(jwt)?.userId : undefined) ?? null;
  });

  // Credentials
  const [credentials, setCredentials] = useState<CredentialStatus | null>(null);
  const [showTokenModal, setShowTokenModal] = useState<'github' | null>(null);
  const [tokenInput, setTokenInput] = useState('');
  const [tokenValidation, setTokenValidation] = useState<{ status: 'idle' | 'loading' | 'valid' | 'invalid'; orgName?: string; repoCount?: number }>({ status: 'idle' });
  const [savingToken, setSavingToken] = useState(false);
  const [revokeTarget, setRevokeTarget] = useState<'github' | null>(null);

  // Redirect non-admins
  useEffect(() => {
    if (!tenantLoading && myTenant && !isAdminRole(myTenant.role)) {
      navigate('/settings');
    }
  }, [tenantLoading, myTenant, navigate]);

  const tenantId = myTenant?.tenantId;

  // Fetch tenant, members, timezones, notification config
  const fetchData = useCallback(() => {
    if (!tenantId) return;
    apiFetch<Tenant>(`/tenants/${tenantId}`).then(t => {
      setTenant(t);
      setOrgName(t.name);
      setTimezone(t.agentConfigOverride?.timezone ?? '');
      setQualityFeedbackEnabled(Boolean(t.qualityFeedbackEnabled));
      setClaudeDesignExportEnabled(Boolean(t.claudeDesignExportEnabled));
    }).catch(() => {});
    apiFetch<TenantMemberRow[]>(`/tenants/${tenantId}/members`).then(setMembers).catch(() => {}).finally(() => setMembersLoading(false));
    apiFetch<{ links: PartnerLinkRow[] }>(`/tenants/${tenantId}/partner-links`).then(r => setPartnerLinks(r.links ?? [])).catch(() => {});
    apiFetch<PendingInvitationRow[]>(`/tenants/${tenantId}/invitations`).then(setPendingInvites).catch(() => {}).finally(() => setPendingInvitesLoading(false));
    apiFetch<NotificationConfig>(`/tenants/${tenantId}/notification-config`).then(setNotifications).catch(() => {});
    apiFetch<CredentialStatus>(`/tenants/${tenantId}/credentials`).then(setCredentials).catch(() => {});
  }, [tenantId]);

  useEffect(() => { fetchData(); }, [fetchData]);
  // Browser-detected timezone used as default if tenant has none saved
  const detectedTz = (() => { try { return Intl.DateTimeFormat().resolvedOptions().timeZone; } catch { return ''; } })();
  const effectiveTimezone = timezone || detectedTz;

  // ── Handlers ─────────────────────────────────────────────────────────────

  const handleSave = async () => {
    if (!tenantId) return;

    setSaving(true);
    setSaveMsg('');
    try {
      await apiFetch(`/tenants/${tenantId}`, {
        method: 'PATCH',
        body: JSON.stringify({
          name: orgName,
          config: { timezone: effectiveTimezone, notifications },
        }),
      });
      setSaveMsg('Organisation settings saved');
      setTimeout(() => setSaveMsg(''), 3000);
    } catch {
      setSaveMsg('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleRoleChange = async (member: TenantMemberRow, newRole: string) => {
    if (!tenantId) return;
    try {
      await apiFetch(`/tenants/${tenantId}/members/${member.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ role: newRole }),
      });
      setMembers(prev => prev.map(m => m.id === member.id ? { ...m, role: newRole } : m));
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to update role');
    }
  };

  // #3229 Story A — open the removal modal and load its preview.
  const openRemove = async (m: TenantMemberRow) => {
    setRemoveTarget(m);
    setRemovePreview(null);
    setRemovePreviewError(null);
    setRemoveError(null);
    setRemoveBlockers([]);
    const request = ++removeRequestRef.current;
    if (!tenantId) return;
    try {
      const preview = await apiFetch<RemovalPreview>(`/tenants/${tenantId}/members/${m.userId}/removal-preview`);
      if (request === removeRequestRef.current) setRemovePreview(preview);
    } catch (err) {
      if (request === removeRequestRef.current) setRemovePreviewError(err instanceof Error ? err.message : 'Could not load what this removal will change.');
    }
  };

  const closeRemove = () => {
    setRemoveTarget(null);
    setRemovePreview(null);
    setRemoveBlockers([]);
    setRemoveError(null);
  };

  const handleRemove = async () => {
    if (!tenantId || !removeTarget) return;
    setRemoveError(null);
    try {
      await apiFetch(`/tenants/${tenantId}/members/${removeTarget.userId}`, { method: 'DELETE' });
      setMembers(prev => prev.filter(m => m.id !== removeTarget.id));
      closeRemove();
    } catch (err) {
      // 409 SOLE_PROJECT_LEAD: show the projects in the modal (a lead may have
      // changed since the preview loaded).
      const blockers = soleLeadProjectsOf(err);
      if (blockers) setRemoveBlockers(blockers);
      setRemoveError(err instanceof Error ? err.message : 'Failed to remove member');
    }
  };

  // #3229 O9 — open the end-partnership confirmation and load its preview.
  const openEnd = async (l: PartnerLinkRow) => {
    setEndError(null);
    setEndPreview(null);
    setEndTarget(l);
    if (!tenantId) return;
    try {
      setEndPreview(await apiFetch<EndPreview>(`/tenants/${tenantId}/partner-links/${l.partnerAccountId}/end-preview`));
    } catch {
      // The preview is advisory: the end still works, and its result lists the projects.
    }
  };

  const handleInvite = async () => {
    if (!tenantId) return;
    setInviting(true);
    setInviteMsg('');
    try {
      await apiFetch(`/tenants/${tenantId}/invitations`, {
        method: 'POST',
        body: JSON.stringify({ email: inviteEmail, role: inviteRole }),
      });
      setInviteMsg(`Invitation sent to ${inviteEmail}`);
      setInviteEmail('');
      setInviteRole('TENANT_MEMBER');
      // #2428 — the new invite otherwise doesn't appear in the Pending
      // Invitations list until a manual reload.
      apiFetch<PendingInvitationRow[]>(`/tenants/${tenantId}/invitations`).then(setPendingInvites).catch(() => {});
      setTimeout(() => { setShowInvite(false); setInviteMsg(''); }, 2000);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to send invitation';
      // apiFetch throws new Error(body.error) on 402, so msg is the exact
      // stable error code. No substring match needed.
      if (msg === ERR_INDIVIDUAL_TIER_SOLO_ONLY) {
        setShowInvite(false);
        setShowUpgradeModal(true);
      } else {
        setInviteMsg(msg);
      }
    } finally {
      setInviting(false);
    }
  };

  const handleValidateToken = async () => {
    if (!tenantId || !showTokenModal) return;
    setTokenValidation({ status: 'loading' });
    try {
      const result = await apiFetch<{ valid: boolean; repoCount?: number; orgName?: string }>(
        `/tenants/${tenantId}/credentials/${showTokenModal}/validate`,
        { method: 'POST', body: JSON.stringify({ token: tokenInput }) },
      );
      setTokenValidation(result.valid
        ? { status: 'valid', orgName: result.orgName, repoCount: result.repoCount }
        : { status: 'invalid' });
    } catch {
      setTokenValidation({ status: 'invalid' });
    }
  };

  const handleSaveToken = async () => {
    if (!tenantId || !showTokenModal) return;
    setSavingToken(true);
    try {
      await apiFetch(`/tenants/${tenantId}/credentials/${showTokenModal}`, {
        method: 'PATCH',
        body: JSON.stringify({ token: tokenInput }),
      });
      setShowTokenModal(null);
      setTokenInput('');
      setTokenValidation({ status: 'idle' });
      apiFetch<CredentialStatus>(`/tenants/${tenantId}/credentials`).then(setCredentials).catch(() => {});
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to save token');
    } finally {
      setSavingToken(false);
    }
  };

  const handleRevoke = async () => {
    if (!tenantId || !revokeTarget) return;
    try {
      await apiFetch(`/tenants/${tenantId}/credentials/${revokeTarget}`, { method: 'DELETE' });
      setRevokeTarget(null);
      apiFetch<CredentialStatus>(`/tenants/${tenantId}/credentials`).then(setCredentials).catch(() => {});
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to revoke');
    }
  };

  const adminCount = members.filter(m => isAdminRole(m.role)).length;

  // ── Render ───────────────────────────────────────────────────────────────

  if (tenantLoading) {
    return (
      <div className="flex min-h-screen" style={{ background: 'var(--bg)' }}>
        <Sidebar />
        <main className="flex-1 px-8 py-6">
          <p style={{ fontFamily: 'var(--font-body)', fontSize: 14, color: 'var(--text-secondary)' }}>Loading...</p>
        </main>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen" style={{ background: 'var(--bg)' }}>
      <Sidebar />
      <main className="flex-1 px-8 py-6">
        <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 24, fontWeight: 700, color: 'var(--text)', marginBottom: 24 }}>
          Settings / {orgLabel}
        </h1>
        <div style={{ maxWidth: 640 }}>

          {/* Organisation Profile / Account Profile (#779) */}
          <div style={cardStyle} data-testid="panel-profile">
            <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 16, fontWeight: 600, color: 'var(--text)', marginBottom: 4 }}>
              {orgLabel} Profile
            </h2>
            <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)', marginBottom: 16 }}>
              {orgLabel === 'Account'
                ? 'Manage your account name, timezone, and notification preferences.'
                : 'Manage your organisation name, timezone, and notification preferences.'}
            </p>

            {/* Name */}
            <label style={labelStyle}>Organisation Name</label>
            <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
              <input
                style={{ ...inputStyle, flex: 1 }}
                value={orgName}
                onChange={e => setOrgName(e.target.value)}
                placeholder="Organisation name"
              />
              <button
                style={{ ...btnPrimary, opacity: saving ? 0.6 : 1 }}
                onClick={() => void handleSave()}
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>

            {/* Timezone */}
            <label style={labelStyle}>Timezone</label>
            <div style={{ marginBottom: 16 }}>
              <TimezoneDropdown
                value={effectiveTimezone}
                onChange={tz => setTimezone(tz)}
              />
            </div>

            {/* Notification Preferences */}
            <label style={{ ...labelStyle, marginBottom: 12 }}>Notification Preferences</label>
            {([
              { key: 'gateFailures' as const, label: 'Gate evaluation failures' },
              { key: 'artefactApprovals' as const, label: 'Artefact approvals' },
              { key: 'agentCompletions' as const, label: 'Agent session completions' },
            ]).map(({ key, label }) => (
              <label key={key} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={notifications[key]}
                  onChange={e => {
                    const updated = { ...notifications, [key]: e.target.checked };
                    setNotifications(updated);
                  }}
                  style={{ accentColor: 'var(--primary)' }}
                />
                <span style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text)' }}>{label}</span>
              </label>
            ))}

            <div style={{ marginTop: 12 }}>
              <button
                style={{ ...btnPrimary, opacity: saving ? 0.6 : 1 }}
                onClick={() => void handleSave()}
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save Preferences'}
              </button>
              {saveMsg && (
                <span style={{
                  marginLeft: 12, fontFamily: 'var(--font-body)', fontSize: 13,
                  color: saveMsg.includes('Failed') ? 'var(--error)' : 'var(--success)', fontWeight: 500,
                }}>
                  {saveMsg}
                </span>
              )}
            </div>
          </div>

          {/* Sprint 12.5 ACC.B6 — Quality feedback toggle (Tenant Admin) */}
          <div style={cardStyle} data-testid="panel-quality-feedback">
            <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 16, fontWeight: 600, color: 'var(--text)', marginBottom: 4 }}>
              Quality Feedback
            </h2>
            <p style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-secondary)', marginBottom: 14 }}>
              Show thumbs-up/down on artefact sections. Helps BluePrint learn what good output looks like for your team.
            </p>
            <label
              data-testid="settings-quality-feedback-toggle-label"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                cursor: 'pointer',
                fontFamily: 'var(--font-body)',
                fontSize: 13,
                color: 'var(--text)',
              }}
            >
              <input
                type="checkbox"
                data-testid="settings-quality-feedback-toggle"
                checked={qualityFeedbackEnabled}
                onChange={e => {
                  const next = e.target.checked;
                  setQualityFeedbackEnabled(next);
                  if (!tenantId) return;
                  apiFetch(`/tenants/${tenantId}`, {
                    method: 'PATCH',
                    body: JSON.stringify({ qualityFeedbackEnabled: next }),
                  })
                    .then(() => {
                      setSaveMsg('Quality feedback ' + (next ? 'enabled' : 'disabled'));
                      window.setTimeout(() => setSaveMsg(''), 1500);
                    })
                    .catch(() => {
                      // Revert on failure so the toggle reflects server state.
                      setQualityFeedbackEnabled(!next);
                      setSaveMsg('Failed to update quality feedback flag');
                      window.setTimeout(() => setSaveMsg(''), 3000);
                    });
                }}
              />
              Enable quality feedback
            </label>
          </div>

          {/* Team Members — hidden for solo plans (#779). The upgrade CTA
              previously rendered here moves to the Plan card (#780). The
              inner isIndividualTier branch is dead under the wrapper but
              kept for now to minimise the diff; can be simplified once
              #780 lands. */}
          {isSectionVisible('team-members', myTenant?.subscriptionTier) && (
          <div style={cardStyle} data-testid="panel-members">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
              <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 16, fontWeight: 600, color: 'var(--text)' }}>
                Team Members
              </h2>
              {isIndividualTier ? (
                <button
                  data-testid="upgrade-invite-btn"
                  style={{
                    ...btnPrimary,
                    background: 'linear-gradient(135deg, var(--primary), #9333ea)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                  }}
                  onClick={() => setShowUpgradeModal(true)}
                >
                  Upgrade to invite teammates
                </button>
              ) : (
                <button
                  style={btnPrimary}
                  onClick={() => setShowInvite(true)}
                >
                  + Invite
                </button>
              )}
            </div>

            {membersLoading ? (
              <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)' }}>Loading...</p>
            ) : members.length === 0 ? (
              <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)' }}>No team members.</p>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, fontFamily: 'var(--font-body)' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid var(--border)' }}>
                    <th style={{ textAlign: 'left', padding: '8px 0', color: 'var(--text-secondary)', fontWeight: 600 }}>Name</th>
                    <th style={{ textAlign: 'left', padding: '8px 0', color: 'var(--text-secondary)', fontWeight: 600 }}>Email</th>
                    <th style={{ textAlign: 'left', padding: '8px 0', color: 'var(--text-secondary)', fontWeight: 600 }}>Role</th>
                    <th style={{ textAlign: 'right', padding: '8px 0', color: 'var(--text-secondary)', fontWeight: 600 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {members.map(m => {
                    const isCurrentUser = myUserId !== null && m.userId === myUserId;
                    const isOnlyAdmin = isAdminRole(m.role) && adminCount <= 1;
                    // APPROVE.1a (#3183) — partner-added members are managed by the
                    // partner link (the API refuses direct changes with 409).
                    const partnerManaged = m.viaPartner === true;
                    return (
                      <tr key={m.id} style={{ borderBottom: '1px solid var(--border)' }}>
                        <td style={{ padding: '10px 0', color: 'var(--text)' }}>
                          {m.userName ?? 'Unknown'}
                          {partnerManaged && (
                            <span data-testid={`member-partner-badge-${m.id}`} title="Added by your delivery partner. Managed through the partnership." style={{ marginLeft: 8, fontSize: 11, fontWeight: 600, padding: '1px 6px', borderRadius: 'var(--radius-sm)', background: 'rgba(124,58,237,0.1)', color: 'var(--primary)' }}>
                              Partner
                            </span>
                          )}
                        </td>
                        <td style={{ padding: '10px 0', color: 'var(--text-secondary)' }}>
                          {m.userEmail ?? '—'}
                        </td>
                        <td style={{ padding: '10px 0' }}>
                          <select
                            value={m.role}
                            onChange={e => void handleRoleChange(m, e.target.value)}
                            disabled={isOnlyAdmin || partnerManaged}
                            style={{
                              fontFamily: 'var(--font-body)', fontSize: 12, fontWeight: 500,
                              padding: '3px 8px', borderRadius: 'var(--radius-sm)',
                              border: '1px solid var(--border)', background: 'var(--surface)',
                              color: isAdminRole(m.role) ? 'var(--primary)' : 'var(--text)',
                              cursor: isOnlyAdmin || partnerManaged ? 'not-allowed' : 'pointer',
                            }}
                          >
                            <option value="TENANT_ADMIN">Admin</option>
                            <option value="TENANT_MEMBER">Member</option>
                          </select>
                        </td>
                        <td style={{ padding: '10px 0', textAlign: 'right' }}>
                          <button
                            style={{ ...btnGhost, opacity: isCurrentUser || partnerManaged ? 0.4 : 1 }}
                            disabled={isCurrentUser || partnerManaged}
                            title={partnerManaged ? 'Managed through the partnership' : undefined}
                            onClick={() => void openRemove(m)}
                          >
                            Remove
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
          )}

          {/* APPROVE.1a (#3183, ruling R-5) — delivery partners linked to this
              workspace. A SELF_SERVICE partnership can be ended here by the
              client's own admin; a partner-MANAGED one is ended by request
              from the Client Portal. */}
          {partnerLinks.length > 0 && (
          <div style={cardStyle} data-testid="panel-delivery-partners">
            <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 16, fontWeight: 600, color: 'var(--text)', marginBottom: 16 }}>
              Delivery Partners
            </h2>
            {partnerLinks.map(l => (
              <div key={l.partnerAccountId} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--border)', fontFamily: 'var(--font-body)', fontSize: 13 }}>
                <span style={{ color: 'var(--text)' }}>
                  {l.partnerName}
                  <span style={{ marginLeft: 8, color: 'var(--text-secondary)' }}>
                    {l.deliveryModel === 'MANAGED' ? 'Manages your workspace' : 'Advises your team'}
                  </span>
                </span>
                {l.deliveryModel === 'SELF_SERVICE' ? (
                  <button style={btnGhost} data-testid={`end-partnership-${l.partnerAccountId}`} onClick={() => void openEnd(l)}>
                    End partnership
                  </button>
                ) : (
                  <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                    {l.openUnlinkRequest ? 'Request to end pending' : 'Ended by request from the Client Portal'}
                  </span>
                )}
              </div>
            ))}
            {endTarget && (
              <div role="dialog" data-testid="end-partnership-confirm" style={{ marginTop: 16, padding: 16, border: '1px solid var(--border)', borderRadius: 'var(--radius)' }}>
                <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text)', margin: '0 0 12px' }}>
                  End the partnership with <strong>{endTarget.partnerName}</strong>? Everyone they added to this workspace loses access immediately. This can&rsquo;t be undone; they would need to invite you again.
                </p>
                {endPreview && endPreview.leaderlessProjects.length > 0 && (
                  <div data-testid="end-partnership-leaderless-preview" style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text)', margin: '0 0 12px' }}>
                    These projects will have no project lead afterwards. Assign a new lead to each of them:
                    <ul style={{ margin: '4px 0 0 18px', padding: 0 }}>
                      {endPreview.leaderlessProjects.map((p) => <li key={p.id}>{p.name}</li>)}
                    </ul>
                  </div>
                )}
                {endError && <p role="alert" style={{ color: 'var(--error)', fontSize: 12, margin: '0 0 8px' }}>{endError}</p>}
                <div style={{ display: 'flex', gap: 8 }}>
                  <button style={btnPrimary} onClick={async () => {
                    try {
                      const result = await apiFetch<{ leaderlessProjects?: ProjectRef[] }>(`/tenants/${tenantId}/partner-links/${endTarget.partnerAccountId}/end`, { method: 'POST', body: JSON.stringify({}) });
                      // #3229 D8 — the projects the partnership's people were the only lead of.
                      setEndLeaderless(result.leaderlessProjects?.length ? { partnerName: endTarget.partnerName, projects: result.leaderlessProjects } : null);
                      setEndTarget(null);
                      setEndPreview(null);
                      fetchData();
                    } catch (err) {
                      setEndError(err instanceof Error ? err.message : 'Could not end the partnership.');
                    }
                  }}>End partnership</button>
                  <button style={btnGhost} onClick={() => { setEndTarget(null); setEndPreview(null); }}>Cancel</button>
                </div>
              </div>
            )}
            {endLeaderless && (
              <div role="status" data-testid="end-partnership-leaderless" style={{ marginTop: 16, padding: 16, border: '1px solid var(--border)', borderRadius: 'var(--radius)', fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text)' }}>
                The partnership with <strong>{endLeaderless.partnerName}</strong> has ended. These projects no longer have a project lead. Assign a new lead to each of them:
                <ul style={{ margin: '4px 0 8px 18px', padding: 0 }}>
                  {endLeaderless.projects.map((p) => <li key={p.id}>{p.name}</li>)}
                </ul>
                <button style={btnGhost} onClick={() => setEndLeaderless(null)}>Dismiss</button>
              </div>
            )}
          </div>
          )}

          {/* Pending Invitations (#2428) — hidden for solo plans, same gate
              as Team Members. Read-only: no Resend/Cancel here. The existing
              resend/cancel routes are keyed by project_invitations.id, which
              IS the accept-invite bearer token (#2210/#2422 precedent) — this
              endpoint deliberately never returns it, so there's no safe value
              to wire those actions to from this list. See #1969. */}
          {isSectionVisible('team-members', myTenant?.subscriptionTier) && (
          <div style={cardStyle} data-testid="panel-invitations">
            <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 16, fontWeight: 600, color: 'var(--text)', marginBottom: 16 }}>
              Pending Invitations
            </h2>

            {pendingInvitesLoading ? (
              <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)' }}>Loading...</p>
            ) : pendingInvites.length === 0 ? (
              <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)' }}>No pending invitations.</p>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, fontFamily: 'var(--font-body)' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid var(--border)' }}>
                    <th style={{ textAlign: 'left', padding: '8px 0', color: 'var(--text-secondary)', fontWeight: 600 }}>Email</th>
                    <th style={{ textAlign: 'left', padding: '8px 0', color: 'var(--text-secondary)', fontWeight: 600 }}>Role</th>
                    <th style={{ textAlign: 'left', padding: '8px 0', color: 'var(--text-secondary)', fontWeight: 600 }}>Sent</th>
                  </tr>
                </thead>
                <tbody>
                  {pendingInvites.map((inv, i) => (
                    <tr key={`${inv.email}-${inv.createdAt}-${i}`} style={{ borderBottom: '1px solid var(--border)' }}>
                      <td style={{ padding: '10px 0', color: 'var(--text)' }}>{inv.email}</td>
                      <td style={{ padding: '10px 0', color: 'var(--text-secondary)' }}>
                        {inv.role === 'TENANT_ADMIN' ? 'Admin' : 'Member'}
                      </td>
                      <td style={{ padding: '10px 0', color: 'var(--text-secondary)' }}>
                        {new Date(inv.createdAt).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          )}

          {/* Integrations */}
          <div style={cardStyle} data-testid="panel-claude-design">
            <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 16, fontWeight: 600, color: 'var(--text)', marginBottom: 4 }}>
              Integrations
            </h2>
            <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)', marginBottom: 16 }}>
              Manage external service connections used by agents.
            </p>

            {/* GitHub */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '12px 0', borderBottom: '1px solid var(--border)' }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                  <span style={{ fontFamily: 'var(--font-display)', fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>GitHub</span>
                  <span style={{
                    fontSize: 11, fontWeight: 600, fontFamily: 'var(--font-body)',
                    padding: '2px 8px', borderRadius: 'var(--radius-sm)',
                    color: credentials?.github.connected ? '#059669' : 'var(--text-secondary)',
                    background: credentials?.github.connected ? 'rgba(5,150,105,0.1)' : 'rgba(0,0,0,0.05)',
                  }}>
                    {credentials?.github.connected ? '✓ Connected' : '✗ Not connected'}
                  </span>
                </div>
                {credentials?.github.connected && (
                  <>
                    <p style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-secondary)', margin: 0 }}>
                      {credentials.github.orgName}
                    </p>
                    <p style={{ fontFamily: 'var(--font-mono, monospace)', fontSize: 12, color: 'var(--text-secondary)', margin: '4px 0 0' }}>
                      Token: {credentials.github.maskedToken}
                    </p>
                  </>
                )}
              </div>
              <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                <button style={btnPrimary} onClick={() => { setShowTokenModal('github'); setTokenInput(''); setTokenValidation({ status: 'idle' }); }}>
                  {credentials?.github.connected ? 'Update Token' : 'Connect'}
                </button>
                {credentials?.github.connected && (
                  <button style={btnGhost} onClick={() => setRevokeTarget('github')}>Revoke</button>
                )}
              </div>
            </div>

            {/* DRV-1 (#2879, #967) — the pasted-token Google Drive card was
                removed: Drive connects with Google sign-in
                (/settings/integrations/gdrive), and the old /credentials/google-drive routes now
                answer 410. */}

            {/* Claude Design — UXV.F4 (#1420). Unlike GitHub/Drive this is a
                feature flag, not a credential, so it uses the instant-save
                toggle pattern (cf. Quality Feedback) rather than connect/revoke.
                Tenant-level default; projects can override it individually. */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '12px 0', borderTop: '1px solid var(--border)' }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                  <span style={{ fontFamily: 'var(--font-display)', fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>Claude Design</span>
                </div>
                <p style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-secondary)', margin: 0, maxWidth: 380 }}>
                  Surface the “Export to Claude Design” action on Phase 2.5 artefacts. Projects can override this default individually.
                </p>
              </div>
              <label
                data-testid="settings-claude-design-export-toggle-label"
                style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', flexShrink: 0, fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text)' }}
              >
                <input
                  type="checkbox"
                  data-testid="settings-claude-design-export-toggle"
                  checked={claudeDesignExportEnabled}
                  onChange={e => {
                    const next = e.target.checked;
                    setClaudeDesignExportEnabled(next);
                    if (!tenantId) return;
                    apiFetch(`/tenants/${tenantId}`, {
                      method: 'PATCH',
                      body: JSON.stringify({ claudeDesignExportEnabled: next }),
                    })
                      .then(() => {
                        setSaveMsg('Claude Design export ' + (next ? 'enabled' : 'disabled'));
                        window.setTimeout(() => setSaveMsg(''), 1500);
                      })
                      .catch(() => {
                        // Revert on failure so the toggle reflects server state.
                        setClaudeDesignExportEnabled(!next);
                        setSaveMsg('Failed to update Claude Design export flag');
                        window.setTimeout(() => setSaveMsg(''), 3000);
                      });
                  }}
                  style={{ accentColor: 'var(--primary)' }}
                />
                {claudeDesignExportEnabled ? 'Enabled' : 'Disabled'}
              </label>
            </div>
          </div>
        </div>

        {/* Invite Modal */}
        {showInvite && (
          <div style={{
            position: 'fixed', inset: 0, zIndex: 100,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'rgba(0,0,0,0.4)',
          }} onClick={() => setShowInvite(false)}>
            <div style={{
              background: 'var(--surface)', borderRadius: 'var(--radius-lg)',
              padding: 24, width: 400, maxHeight: '85vh', overflowY: 'auto',
              boxShadow: 'var(--shadow-lg)',
            }} onClick={e => e.stopPropagation()}>
              <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 16, fontWeight: 600, color: 'var(--text)', marginBottom: 16 }}>
                Invite Team Member
              </h3>
              <label style={labelStyle}>Email Address</label>
              <input
                style={{ ...inputStyle, marginBottom: 12 }}
                type="email"
                value={inviteEmail}
                onChange={e => setInviteEmail(e.target.value)}
                placeholder="colleague@company.com"
              />
              <label style={labelStyle}>Role</label>
              <select
                value={inviteRole}
                onChange={e => setInviteRole(e.target.value as 'TENANT_ADMIN' | 'TENANT_MEMBER')}
                style={{ ...inputStyle, marginBottom: 16 }}
              >
                <option value="TENANT_MEMBER">Member</option>
                <option value="TENANT_ADMIN">Admin</option>
              </select>
              {inviteMsg && (
                <p style={{
                  fontFamily: 'var(--font-body)', fontSize: 13, marginBottom: 12,
                  color: inviteMsg.includes('sent') ? 'var(--success)' : 'var(--error)',
                }}>
                  {inviteMsg}
                </p>
              )}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                <button style={btnGhost} onClick={() => setShowInvite(false)}>Cancel</button>
                <button
                  style={{ ...btnPrimary, opacity: inviting || !inviteEmail ? 0.6 : 1 }}
                  onClick={() => void handleInvite()}
                  disabled={inviting || !inviteEmail}
                >
                  {inviting ? 'Sending...' : 'Send Invite'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Update Token Modal */}
        {showTokenModal && (
          <div style={{
            position: 'fixed', inset: 0, zIndex: 100,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'rgba(0,0,0,0.4)',
          }} onClick={() => setShowTokenModal(null)}>
            <div style={{
              background: 'var(--surface)', borderRadius: 'var(--radius-lg)',
              padding: 24, width: 440, maxHeight: '85vh', overflowY: 'auto',
              boxShadow: 'var(--shadow-lg)',
            }} onClick={e => e.stopPropagation()}>
              <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 16, fontWeight: 600, color: 'var(--text)', marginBottom: 16 }}>
                GitHub Personal Access Token
              </h3>
              <label style={labelStyle}>
                Personal Access Token
              </label>
              <PasswordInput
                style={{ ...inputStyle, fontFamily: 'var(--font-mono, monospace)' }}
                wrapperStyle={{ marginBottom: 8 }}
                value={tokenInput}
                onChange={e => { setTokenInput(e.target.value); setTokenValidation({ status: 'idle' }); }}
                placeholder="ghp_xxxxxxxxxxxxxxxxxxxx"
              />
              {showTokenModal === 'github' && (
                <p style={{ fontFamily: 'var(--font-body)', fontSize: 11, color: 'var(--text-secondary)', margin: '0 0 12px' }}>
                  Required scopes: <strong>repo</strong>, <strong>workflow</strong>.{' '}
                  <a href="https://github.com/settings/tokens" target="_blank" rel="noopener noreferrer"
                    style={{ color: 'var(--primary)', textDecoration: 'none' }}>
                    How to create a token →
                  </a>
                </p>
              )}

              {tokenValidation.status === 'valid' && (
                <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: '#059669', marginBottom: 12 }}>
                  ✓ Valid{tokenValidation.orgName ? ` — ${tokenValidation.repoCount ?? 0} repositories, org: ${tokenValidation.orgName}` : ''}
                </p>
              )}
              {tokenValidation.status === 'invalid' && (
                <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--error)', marginBottom: 12 }}>
                  ✗ Invalid token or insufficient scopes
                </p>
              )}

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                <button style={btnGhost} onClick={() => setShowTokenModal(null)}>Cancel</button>
                <button
                  style={{ ...btnPrimary, background: 'var(--text-secondary)', opacity: !tokenInput || tokenValidation.status === 'loading' ? 0.6 : 1 }}
                  onClick={() => void handleValidateToken()}
                  disabled={!tokenInput || tokenValidation.status === 'loading'}
                >
                  {tokenValidation.status === 'loading' ? 'Validating...' : 'Validate'}
                </button>
                <button
                  style={{ ...btnPrimary, opacity: tokenValidation.status !== 'valid' || savingToken ? 0.6 : 1 }}
                  onClick={() => void handleSaveToken()}
                  disabled={tokenValidation.status !== 'valid' || savingToken}
                >
                  {savingToken ? 'Saving...' : 'Save Token'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Revoke Confirmation Modal */}
        {revokeTarget && (
          <div style={{
            position: 'fixed', inset: 0, zIndex: 100,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'rgba(0,0,0,0.4)',
          }} onClick={() => setRevokeTarget(null)}>
            <div style={{
              background: 'var(--surface)', borderRadius: 'var(--radius-lg)',
              padding: 24, width: 400, maxHeight: '85vh', overflowY: 'auto',
              boxShadow: 'var(--shadow-lg)',
            }} onClick={e => e.stopPropagation()}>
              <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 16, fontWeight: 600, color: 'var(--text)', marginBottom: 8 }}>
                Revoke GitHub Token
              </h3>
              <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)', marginBottom: 20 }}>
                Revoke GitHub token? The Code Review Agent requires this connection.
              </p>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                <button style={btnGhost} onClick={() => setRevokeTarget(null)}>Cancel</button>
                <button
                  style={{ ...btnPrimary, background: 'var(--error)' }}
                  onClick={() => void handleRevoke()}
                >
                  Revoke
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Remove Confirmation Modal */}
        {removeTarget && (
          <div style={{
            position: 'fixed', inset: 0, zIndex: 100,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'rgba(0,0,0,0.4)',
          }} onClick={closeRemove}>
            <div style={{
              background: 'var(--surface)', borderRadius: 'var(--radius-lg)',
              padding: 24, width: 400, maxHeight: '85vh', overflowY: 'auto',
              boxShadow: 'var(--shadow-lg)',
            }} onClick={e => e.stopPropagation()}>
              <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 16, fontWeight: 600, color: 'var(--text)', marginBottom: 8 }}>
                Remove Member
              </h3>
              <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)', marginBottom: 12 }}>
                Remove <strong>{removeTarget.userName ?? removeTarget.userEmail}</strong> from{' '}
                <strong>{tenant?.name ?? 'this organisation'}</strong>? They will lose access immediately,
                including to every project in this organisation. Adding them back later does not restore their project roles.
              </p>
              {/* #3229 Story A — ruling 1: list the projects and every role (D1). */}
              {!removePreview && !removePreviewError && (
                <p style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-secondary)', marginBottom: 12 }}>Checking their projects…</p>
              )}
              {removePreviewError && (
                <p role="alert" style={{ color: 'var(--error)', fontSize: 12, marginBottom: 12 }}>{removePreviewError}</p>
              )}
              {removePreview && removePreview.projects.length > 0 && (
                <div data-testid="remove-member-projects" style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text)', marginBottom: 12 }}>
                  They will be removed from these projects:
                  <ul style={{ margin: '4px 0 0 18px', padding: 0 }}>
                    {removePreview.projects.map((p) => (
                      <li key={p.id}>
                        {p.name}{p.deleted ? ' (deleted)' : p.status === 'ARCHIVED' ? ' (archived)' : ''}: {p.roles.map((r) => `${r.role}${r.memberType === 'DELEGATE' ? ' (delegate)' : ''}`).join(', ')}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {removePreview && (removePreview.pendingInvitations > 0 || removePreview.liveLinks > 0 || removePreview.stakeholderProjects > 0) && (
                <p style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-secondary)', marginBottom: 12 }}>
                  Their pending invitations, unused approval links and client-stakeholder access in this organisation are cancelled too.
                </p>
              )}
              {(() => {
                const blockers = removeBlockers.length > 0 ? removeBlockers : (removePreview?.soleLeadProjects ?? []);
                if (blockers.length === 0) return null;
                return (
                  <div role="alert" data-testid="remove-member-sole-lead" style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--error)', marginBottom: 12 }}>
                    They are the only lead on these projects. Assign a new lead first:
                    <ul style={{ margin: '4px 0 0 18px', padding: 0 }}>
                      {blockers.map((p) => <li key={p.id}>{p.name}</li>)}
                    </ul>
                  </div>
                );
              })()}
              {removePreview?.lastAdmin && (
                <p role="alert" style={{ color: 'var(--error)', fontSize: 13, marginBottom: 12 }}>They are this organisation&rsquo;s last administrator. Make someone else an administrator first.</p>
              )}
              {removeError && removeBlockers.length === 0 && (
                <p role="alert" style={{ color: 'var(--error)', fontSize: 12, marginBottom: 12 }}>{removeError}</p>
              )}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                <button style={btnGhost} onClick={closeRemove}>Cancel</button>
                {(() => {
                  const loading = !removePreview && !removePreviewError;
                  const blocked = removeBlockers.length > 0 || (removePreview?.soleLeadProjects.length ?? 0) > 0 || !!removePreview?.lastAdmin;
                  return (
                    <button
                      style={{ ...btnPrimary, background: 'var(--error)', opacity: blocked || loading ? 0.4 : 1, cursor: blocked || loading ? 'not-allowed' : 'pointer' }}
                      disabled={blocked || loading}
                      title={blocked ? 'Assign a new lead first' : undefined}
                      onClick={() => void handleRemove()}
                    >
                      Remove
                    </button>
                  );
                })()}
              </div>
            </div>
          </div>
        )}

        {/* Upgrade Modal (Individual tier solo enforcement) */}
        {showUpgradeModal && (
          <div style={{
            position: 'fixed', inset: 0, zIndex: 100,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'rgba(0,0,0,0.4)',
          }} onClick={() => setShowUpgradeModal(false)}>
            <div
              data-testid="upgrade-modal"
              style={{
                background: 'var(--surface)', borderRadius: 'var(--radius-lg)',
                padding: 32, width: 440, maxHeight: '85vh', overflowY: 'auto',
                boxShadow: 'var(--shadow-lg)',
              }}
              onClick={e => e.stopPropagation()}
            >
              <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 18, fontWeight: 700, color: 'var(--text)', marginBottom: 8 }}>
                Team collaboration requires Starter
              </h3>
              <p style={{ fontFamily: 'var(--font-body)', fontSize: 14, color: 'var(--text-secondary)', marginBottom: 16, lineHeight: 1.5 }}>
                Individual plan is solo-only. Upgrade to Starter to:
              </p>
              <ul style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text)', marginBottom: 20, paddingLeft: 20, lineHeight: 1.8 }}>
                <li>Invite unlimited team members</li>
                <li>Assign roles (PM, Developer, QA Engineer)</li>
                <li>Collaborate on projects together</li>
              </ul>
              <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)', marginBottom: 24 }} data-testid="upgrade-modal-price">
                {/* CFG.I18N.A2 — region-aware Starter price. Tenant timezone
                    exists here (post-auth) so we prefer it over the browser
                    default; locale is still the first-priority signal. */}
                {formatTierPrice(
                  'STARTER',
                  detectCurrency({
                    locale: i18n.language,
                    timezone: myTenant?.timezone ?? null,
                  }),
                )} &mdash; cancel anytime.
              </p>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                <button style={btnGhost} onClick={() => setShowUpgradeModal(false)}>Maybe later</button>
                <button
                  style={{ ...btnPrimary, background: 'linear-gradient(135deg, var(--primary), #9333ea)' }}
                  onClick={() => navigate('/upgrade')}
                >
                  Upgrade to Starter →
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
